import { inject, Injectable } from '@angular/core';

import { OktaAuthCoreInterface } from '@okta/okta-auth-js';
import { CancellationUrlParamType } from '@ph-env/model/environment.model';
import { BehaviorSubject } from 'rxjs';

import { UserStore, UserStoreType } from '@ph-store/user/user.store';

import { ConfigService } from './config/config.service';
import { OktaService } from './okta/okta.service';

export type CancellationUrlType = 'home' | 'requestInitiated' | 'refundIssued';

@Injectable({ providedIn: 'root' })
export class CancellationService {
  userStore: UserStoreType = inject(UserStore);

  url: string | undefined;
  oktaAuth$: BehaviorSubject<OktaAuthCoreInterface | undefined> = new BehaviorSubject<
    OktaAuthCoreInterface | undefined
  >(undefined);
  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private oktaService = inject(OktaService);
  private configService = inject(ConfigService);
  private environment = this.configService.environment;

  constructor() {
    if (
      this.environment.cancellationUrlConfig.source === 'ENVIRONMENT' &&
      this.environment.cancellationUrlConfig.params.includes('OKTA-TOKEN')
    ) {
      this.oktaAuth$.next(this.oktaService.getOktaAuth());
    }
  }

  getCancellationUrl(type: CancellationUrlType) {
    const extraQueryParams = type === 'home' ? '' : `&requestType=${type}`;
    const cancellationURL = this.userStore.user()?.cancellationURL;
    const dealerNumber = this.userStore.user()?.cmsDealerNumber;
    const oktaAuth = this.oktaAuth$.getValue();
    const valid =
      !!cancellationURL ||
      (this.environment.cancellationUrlConfig.source === 'ENVIRONMENT' &&
        !!dealerNumber &&
        !!oktaAuth?.getAccessToken());

    if (!valid) {
      return '';
    }
    let url: string | undefined;
    if (this.environment.cancellationUrlConfig.source === 'SERVER') {
      url = cancellationURL;
      url += this._urlTimestamp(url || '') + extraQueryParams;
    } else if (this.environment.cancellationUrlConfig.source === 'ENVIRONMENT' && this.environment.cancellationUrl) {
      url = this.environment.cancellationUrl + this._buildParams(dealerNumber || '', oktaAuth?.getAccessToken());
      url += this._urlTimestamp(url) + extraQueryParams;
    } else {
      console.error('Invalid cancellation portal configuration', {
        source: this.environment.cancellationUrlConfig.source,
        url: this.environment.cancellationUrl,
      });
      url = 'about:blank';
    }
    this.url = url;

    return url;
  }

  private _buildParams(dealerNumber: string, oktaAuthToken: string | null | undefined): string {
    const params = this.environment.cancellationUrlConfig.params;

    return params && params.length > 0
      ? params
          .map((param: CancellationUrlParamType) => {
            switch (param) {
              case 'OKTA-TOKEN':
                return oktaAuthToken ? 'token=' + oktaAuthToken : '';
              case 'LANGUAGE-ID':
                return 'languageid=' + ConfigService.getLanguage();
              case 'DEALER-ID':
                return 'dealerNumber=' + dealerNumber;
            }
          })
          .reduce((res: string, curr) => (curr ? res.concat(curr + '&') : res), '?')
          .slice(0, -1)
      : '';
  }

  private _urlTimestamp(url: string): string {
    return `${url.includes('?') ? '&' : '?'}timestamp=${Date.now()}`;
  }
}
